'use client';
import { resolveImageAlt } from '@eliadmin/core/src/utils/image-alt-utils';

import type { CategoriesEntity } from '@eliadmin/core/src/api/general/types/general-layout-types';
import {
  ContactAttribute,
  ContactAttributeList,
} from '@eliadmin/core/src/components/contact/contact-attribute-list';
import { SocialIcons } from '@eliadmin/core/src/components/header/social-icons';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from '@eliadmin/core/src/components/ui/accordion';
import { Separator } from '@eliadmin/core/src/components/ui/separator';
import {
  Sheet,
  SheetClose,
  SheetContent,
  SheetTrigger,
  SheetTitle,
} from '@eliadmin/core/src/components/ui/sheet';
import { CONTACT_CONFIG } from '@eliadmin/core/src/constants/contact-config';
import { HEADER_CONFIG } from '@eliadmin/core/src/constants/header-config';
import { useTranslations } from '@eliadmin/core/src/hooks/use-translations';
import { useGlobalStore } from '@eliadmin/core/src/stores/global-store';
import { cn } from '@eliadmin/core/src/utils/style-utils';
import { getSafeHref, isRelativeHref } from '@eliadmin/core/src/utils/url-utils';
import Link from '@eliadmin/core/src/components/link';
import { LanguageSelect } from './language-select';
import { MdOutlineLocalPhone } from 'react-icons/md';
import { FaRegEnvelope } from 'react-icons/fa';
import { HtmlDescription } from '../homepage/shared/html-description';

import Image from '@eliadmin/core/src/components/image/image-wrapped';
import { ROUTES } from '@eliadmin/core/src/constants/routes';
import { LuMenu } from 'react-icons/lu';

export type MobileSidebarProps = {
  className?: {
    trigger?: string;
    content?: string;
    firstSeparator?: string;
    menuList?: string;
    menuLink?: string;
    menuGroup?: string;
    menuSeparator?: string;
    buttonWrapper?: string;
    button?: string;
    iconsWrapper?: string;
    icon?: string;
  };
  side?: 'right' | 'left' | 'bottom' | 'top';
  firstSeparator?: boolean;
  menu?: {
    type: 'page';
    name: string;
    path?: string;
  }[];
  menuArrow?: boolean;
  menuSeparator?: boolean;
  categories?: CategoriesEntity[];
};

const MobileSidebar = (props: MobileSidebarProps) => {
  const generalLayout = useGlobalStore(state => state.generalLayout);
  const { t } = useTranslations();

  const categories = props.categories || generalLayout.header?.categories;

  return (
    <>
      <Sheet>
        <SheetTrigger
          aria-label="Menu"
          className={cn('text-primary', 'ea-mobile-sidebar__trigger', props.className?.trigger)}
        >
          <LuMenu size={20} aria-hidden="true" />
        </SheetTrigger>
        <SheetContent
          aria-describedby={undefined}
          side={'left'}
          className={cn(
            'flex w-screen flex-col items-start justify-start gap-8 overflow-scroll bg-white px-4 pt-24',
            'ea-mobile-sidebar__content',
            'ea-hide-scrollbar',
            props.className?.content,
          )}
          closeIcon={{
            wrapper: 'right-4 top-4',
            icon: 'text-primary h-6 w-6 font-bold',
          }}
        >
          <SheetTitle className="sr-only">Menu</SheetTitle>
          <div className="absolute right-20 top-4">
            <LanguageSelect />
          </div>
          <div className="flex w-full flex-col items-start justify-start gap-4">
            {props.menu?.map((item, index) => {
              const Comp = item.path
                ? isRelativeHref(getSafeHref(item.path))
                  ? Link
                  : 'a'
                : 'span';
              return (
                <>
                  {item.name === t('text150') ? (
                    <Accordion type="single" collapsible className="w-full border-none p-0">
                      <AccordionItem value={t('text150')} className="w-full border-none p-0">
                        <AccordionTrigger className="w-full border-none p-0 text-lg font-normal uppercase">
                          {t('text150')}
                        </AccordionTrigger>
                        <AccordionContent className="flex flex-col items-start justify-start gap-4 pl-4 pt-4">
                          {categories &&
                            categories.map(category => (
                              <SheetClose asChild key={category.slug}>
                                <Link
                                  prefetchOnHover
                                  prefetch={false}
                                  href={ROUTES.CATEGORY({ slug: category.slug, page: 1 })}
                                  className="flex items-center justify-start gap-4 text-lg font-semibold uppercase text-foreground"
                                >
                                  <Image
                                    src={category?.image?.url || '/images/placeholder.png'}
                                    alt={resolveImageAlt(category?.image, category?.name)}
                                    width={48}
                                    height={48}
                                  />
                                  {category.name}
                                </Link>
                              </SheetClose>
                            ))}
                        </AccordionContent>
                      </AccordionItem>
                    </Accordion>
                  ) : (
                    <SheetClose key={index} asChild>
                      <Comp
                        href={item.path ? getSafeHref(item.path) : (undefined as any)}
                        className="flex w-full"
                        prefetch={false}
                      >
                        <div className={cn('text-lg font-normal uppercase', '')}>{item.name}</div>
                      </Comp>
                    </SheetClose>
                  )}
                </>
              );
            })}
          </div>
          <Separator className="h-[1px] w-full bg-border" />
          <div className="flex w-full flex-col items-start justify-start gap-4">
            <Link
              prefetchOnHover
              prefetch={false}
              href={`tel:${generalLayout?.contact?.phone}`}
              target="_blank"
              className="flex items-center justify-start gap-2 text-lg font-light text-primary lg:text-xl xl:text-2xl"
            >
              <MdOutlineLocalPhone size={20} /> {generalLayout?.contact?.phone}
            </Link>
            <Link
              prefetchOnHover
              prefetch={false}
              href={`mailto:${generalLayout?.contact?.email}`}
              target="_blank"
              className="flex items-center justify-start gap-2 text-lg font-light text-primary lg:text-xl xl:text-2xl"
            >
              <FaRegEnvelope size={20} /> {generalLayout?.contact?.email}
            </Link>
          </div>
          <Separator className="h-[1px] w-full bg-border" />
          <div className="flex w-full flex-col items-start justify-start gap-6">
            <p className="text-2xl font-medium uppercase text-foreground">SHOWROOM</p>
            <div className="flex w-full flex-col items-start justify-start gap-1">
              <p className="text-lg font-light text-foreground lg:text-xl xl:text-2xl">
                {generalLayout?.contact?.address}
              </p>
              <p className="text-lg font-light text-foreground lg:text-xl xl:text-2xl">
                {generalLayout?.contact?.postcode} {generalLayout?.contact?.city}
              </p>
            </div>
            <div className="flex w-full flex-col items-start justify-start gap-1">
              <h4 className="text-lg font-light text-[#999999] lg:text-xl xl:text-2xl">
                {t('text690')}
              </h4>
              <HtmlDescription
                content={generalLayout?.footer?.text ?? ''}
                color="foreground"
                size="large"
              />
            </div>
          </div>
          <SocialIcons />
        </SheetContent>
      </Sheet>
    </>
  );
};

export default MobileSidebar;
